export function buildTerritorialScoring(item = {}) {

  // Inferir campos cognitivos faltantes desde riesgo/influencia/estado,
  // pero registrando CUÁLES fueron inferidos para no tratarlos como
  // datos reales declarados por el analista.
  const { enriched, inferidos, totalCognitivos } = inferirCamposCognitivos(item);

  const IST = calculateIST(enriched);
  const IAT = calculateIAT(enriched);
  const IPN = calculateIPN(enriched);
  const ICT = calculateICT(enriched);

  const IVTBruto = Math.round(
    (IST + IAT + IPN + ICT) / 4
  );

  // Completitud: proporción de campos cognitivos REALES (no inferidos).
  // 1 = todo declarado por el analista. 0 = todo inferido desde dropdowns.
  const completitud = totalCognitivos > 0
    ? (totalCognitivos - inferidos) / totalCognitivos
    : 0;

  // Factor de descuento: un IVT construido mayormente sobre inferencias
  // no debe inflarse igual que uno construido sobre evidencia declarada.
  // Va de 0.7 (todo inferido) a 1.0 (todo declarado).
  const factorConfianza = 0.7 + (0.3 * completitud);

  const IVT = clamp(Math.round(IVTBruto * factorConfianza));

  return {
    IST,
    IAT,
    IPN,
    ICT,
    IVT,
    // Metadatos de origen (no rompen nada; los consume la confianza cognitiva)
    _IVTBruto: IVTBruto,
    _inferidos: inferidos,
    _totalCognitivos: totalCognitivos,
    _completitud: Math.round(completitud * 100),
    _factorConfianza: Math.round(factorConfianza * 100) / 100
  };
}

// ─────────────────────────────────────────────
// INFERENCIA COGNITIVA
// Cuando faltan campos cognitivos, los deduce desde
// riesgo, influencia y estado, y CUENTA cuántos infirió.
// ─────────────────────────────────────────────

function inferirCamposCognitivos(item) {
  const riesgo = String(item.riesgo || '').toLowerCase();
  const influencia = String(item.influencia || '').toLowerCase();
  const estado = String(item.estado || '').toLowerCase();

  // Inferir sensibilidadTerritorial
  const sensibilidadInferida = (() => {
    if (estado.includes('crítico') || riesgo.includes('crítico')) return 'Crítica';
    if (riesgo.includes('alto') || estado.includes('critico')) return 'Alta';
    if (riesgo.includes('medio') || estado.includes('neutral')) return 'Media';
    if (riesgo.includes('bajo') || estado.includes('positivo')) return 'Baja';
    return '';
  })();

  // Inferir activacionTerritorial
  const activacionInferida = (() => {
    if (riesgo.includes('crítico') || estado.includes('crítico')) return 'Alta';
    if (riesgo.includes('alto')) return 'Media';
    if (riesgo.includes('medio')) return 'Baja';
    return '';
  })();

  // Inferir capacidadPropagacion desde influencia
  const propagacionInferida = (() => {
    if (influencia.includes('alta')) return 'Alta';
    if (influencia.includes('media')) return 'Media';
    if (influencia.includes('baja')) return 'Baja';
    return '';
  })();

  // Inferir convergenciaNarrativa desde influencia + riesgo
  const convergenciaInferida = (() => {
    if (influencia.includes('alta') && riesgo.includes('alto')) return 'Alta';
    if (influencia.includes('alta') || riesgo.includes('alto')) return 'Media';
    if (influencia.includes('media')) return 'Baja';
    return '';
  })();

  // Inferir exposicionTerritorial desde influencia
  const exposicionInferida = (() => {
    if (influencia.includes('alta')) return 'Alta';
    if (influencia.includes('media')) return 'Media';
    return '';
  })();

  // Para cada campo cognitivo: usar el declarado si existe; si no, el inferido.
  // Y contar cuántos tuvimos que inferir.
  // historialTerritorial y capacidadMovilizacion no se infieren:
  // o los declara el analista, o simplemente no suman. Por eso su
  // 'inferido' es '' (vacío) — si faltan, no cuentan como inferidos.
  const campos = [
    { key: 'sensibilidadTerritorial', inferido: sensibilidadInferida },
    { key: 'activacionTerritorial',   inferido: activacionInferida },
    { key: 'capacidadPropagacion',    inferido: propagacionInferida },
    { key: 'convergenciaNarrativa',   inferido: convergenciaInferida },
    { key: 'exposicionTerritorial',   inferido: exposicionInferida },
    { key: 'historialTerritorial',    inferido: '' },
    { key: 'capacidadMovilizacion',   inferido: '' }
  ];

  const enriched = { ...item };
  let inferidos = 0;

  for (const { key, inferido } of campos) {
    const declarado = item[key];
    if (declarado && String(declarado).trim()) {
      enriched[key] = declarado;            // dato real del analista
    } else {
      enriched[key] = inferido;             // dato inferido
      if (inferido) inferidos += 1;         // contar solo si se infirió algo
    }
  }

  return {
    enriched,
    inferidos,
    totalCognitivos: campos.length
  };
}

// ─────────────────────────────────────────────
// MOTORES DE SCORING
// ─────────────────────────────────────────────

function calculateIST(item) {
  let score = 20;

  if (item.sensibilidadTerritorial === 'Media')    score += 20;
  if (item.sensibilidadTerritorial === 'Alta')     score += 40;
  if (item.sensibilidadTerritorial === 'Crítica')  score += 60;

  if (item.exposicionTerritorial === 'Alta')       score += 20;
  if (item.exposicionTerritorial === 'Crítica')    score += 30;

  // Historial territorial: cicatriz de fondo (sensibilidad estructural).
  // Pesos moderados — es contexto histórico, no señal del presente.
  if (item.historialTerritorial === 'Media')       score += 10;
  if (item.historialTerritorial === 'Alta')        score += 20;
  if (item.historialTerritorial === 'Crítica')     score += 30;

  return clamp(score);
}

function calculateIAT(item) {
  let score = 10;

  if (item.activacionTerritorial === 'Media')    score += 20;
  if (item.activacionTerritorial === 'Alta')     score += 40;
  if (item.activacionTerritorial === 'Crítica')  score += 60;

  // Capacidad de movilización: potencial de poner gente a actuar.
  // Peso medio — es potencial latente, menor que la activación directa.
  if (item.capacidadMovilizacion === 'Media')    score += 15;
  if (item.capacidadMovilizacion === 'Alta')     score += 30;
  if (item.capacidadMovilizacion === 'Crítica')  score += 45;

  return clamp(score);
}

function calculateIPN(item) {
  let score = 15;

  if (item.capacidadPropagacion === 'Media')    score += 20;
  if (item.capacidadPropagacion === 'Alta')     score += 40;
  if (item.capacidadPropagacion === 'Crítica')  score += 60;

  if (item.presionRRSS === 'Alta')              score += 15;

  return clamp(score);
}

function calculateICT(item) {
  let score = 10;

  if (item.convergenciaNarrativa === 'Media')    score += 20;
  if (item.convergenciaNarrativa === 'Alta')     score += 40;
  if (item.convergenciaNarrativa === 'Crítica')  score += 60;

  if (item.legitimidadTerritorial === 'Alta')    score += 15;
  if (item.legitimidadTerritorial === 'Crítica') score += 25;

  return clamp(score);
}

function clamp(value) {
  return Math.max(0, Math.min(100, value));
}